import { useTranslation } from 'react-i18next';
import type { SmsAnalyzeResponse } from '../../services/api';
import { AnalysisDetails } from './AnalysisDetails';

interface ResultDisplayProps {
  result: SmsAnalyzeResponse;
}

export function ResultDisplay({ result }: ResultDisplayProps) {
  const { t } = useTranslation();

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'safe':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'inconclusive':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'suspicious':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'high_risk':
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level) {
      case 'safe':
        return '✓';
      case 'inconclusive':
        return '❓';
      case 'suspicious':
        return '⚠';
      case 'high_risk':
      case 'critical':
        return '⛔';
      default:
        return 'ℹ';
    }
  };

  return (
    <div className="space-y-4">
      {/* T026B: AI Unavailable Banner */}
      {result.metadata?.aiUnavailable && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div className="flex-1 text-sm text-blue-900">
              <p className="font-medium">{t('sms.aiUnavailable.title', 'AI Analysis Unavailable')}</p>
              <p className="mt-1">{t('sms.aiUnavailable.message', 'Results based on heuristic analysis only.')}</p>
            </div>
          </div>
        </div>
      )}

      <div className={`border-2 rounded-lg p-6 ${getRiskColor(result.riskLevel)}`}>
        <div className="flex items-start gap-3">
          <span className="text-3xl">{getRiskIcon(result.riskLevel)}</span>
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-2">
              {t(result.summary.titleKey, result.summary.params)}
            </h3>
            <p className="text-sm">
              {t(result.summary.messageKey, result.summary.params)}
            </p>
          </div>
        </div>
        
        {result.confidence > 0 && (
          <div className="mt-4 text-xs opacity-75">
            {t('sms.confidence')}: {Math.round(result.confidence * 100)}%
          </div>
        )}
      </div>

      {result.highlights && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium mb-2">{t('sms.detectedThreats')}</h4>
          {result.highlights.urls.length > 0 && (
            <div className="mb-2">
              <div className="text-sm font-medium mb-1">{t('sms.urlsFound')}</div>
              <span className="text-sm font-medium">{t('sms.urls')}: </span>
              <span className="text-sm">{result.highlights.urls.join(', ')}</span>
            </div>
          )}
          {result.highlights.brands.length > 0 && (
            <div className="mb-2">
              <span className="text-sm font-medium">{t('sms.brands')}: </span>
              <span className="text-sm">{result.highlights.brands.join(', ')}</span>
            </div>
          )}
          {result.highlights.threats.length > 0 && (
            <div>
              <span className="text-sm font-medium">{t('sms.threats')}: </span>
              <span className="text-sm">
                {result.highlights.threats.map(threat => t(`THREAT_${threat}`, threat)).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* T026: Display AI Analysis Details */}
      {result.analysis && (
        <AnalysisDetails
          reasonItems={result.analysis.reasonItems}
          mappedScamTypeKey={result.analysis.mappedScamTypeKey}
          mappedTacticKeys={result.analysis.mappedTacticKeys}
        />
      )}

      {result.guidance.prevention.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium mb-3">{t('sms.preventionAdvice')}</h4>
          <ul className="space-y-2">
            {result.guidance.prevention
              .sort((a, b) => a.priority - b.priority)
              .map((step) => (
                <li key={step.id} className="flex items-start gap-2 text-sm">
                  <span className="text-blue-600">•</span>
                  {step.link ? (
                    <a href={step.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {t(step.labelKey)}
                    </a>
                  ) : (
                    <span>{t(step.labelKey)}</span>
                  )}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
